import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Box,
    Paper,
    Typography,
    Divider,
    IconButton,
    Grid,
    CircularProgress,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { api_Routes } from '../../../tools/api_Routes';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const EmailTemplate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedIds = [], selectAllMode = false, filters = {}, template } = location.state || {};
    const token = localStorage.getItem("token");

    const [saveTemplate, setSaveTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateData, setTemplateData] = useState({
        subject: '',
        title: 'Social Bulk Market',
        sections: [
            {
                title: '',
                description: '',
                imageUrl: '',
                imageFile: null,
                buttonText: '',
                buttonLink: ''
            }
        ],
        isHtml: true
    });
    const [uploading, setUploading] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    useEffect(() => {
        if (template) {
            setTemplateData({
                subject: template.subject,
                title: template.title,
                sections: template.sections.map(section => ({
                    title: section.title,
                    description: section.description,
                    imageUrl: section.image,
                    imageFile: null,
                    buttonText: section.button || '',
                    buttonLink: section.link || ''
                })),
                isHtml: template.is_html
            });
            if (template.id) {
                setSaveTemplate(true);
                setTemplateName(template.name);
            }
        }
    }, [template]);

    const handleAddSection = () => {
        setTemplateData(prev => ({
            ...prev,
            sections: [
                ...prev.sections,
                {
                    title: '',
                    description: '',
                    imageUrl: '',
                    imageFile: null,
                    buttonText: '',
                    buttonLink: ''
                }
            ]
        }));
    };

    const handleRemoveSection = (index) => {
        if (templateData.sections.length <= 1) return;
        setTemplateData(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index)
        }));
    };

    const handleSectionChange = (index, field, value) => {
        setTemplateData(prev => {
            const updatedSections = [...prev.sections];
            updatedSections[index][field] = value;
            return { ...prev, sections: updatedSections };
        });
    };

    const handleImageChange = (index, file) => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            handleSectionChange(index, 'imageUrl', e.target.result);
        };
        reader.readAsDataURL(file);

        handleSectionChange(index, 'imageFile', file);
    };

    const generateHtmlFromTemplate = (sections) => {
        return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333; line-height: 1.5; }
          .header { font-size: 28px; font-weight: bold; margin-bottom: 30px; color: #2c3e50; text-align: center; }
          .section { display: flex; margin-bottom: 30px; gap: 20px; align-items: center; }
          .section-content { flex: 1; }
          .section-image { width: 150px; height: 150px; object-fit: cover; border-radius: 5px; }
          .section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #2c3e50; }
          .section-description { margin-bottom: 15px; font-size: 14px; }
          .button { display: inline-block; background-color: #3498db; color: white; padding: 8px 16px; text-decoration: none; border-radius: 5px; font-size: 14px; font-weight: bold; }
          .footer { font-size: 12px; color: #7f8c8d; margin-top: 30px; border-top: 1px solid #eee; padding-top: 15px; text-align: center; }
          @media (max-width: 480px) { .section { flex-direction: column; } .section-image { width: 100%; height: auto; } }
        </style>
      </head>
      <body>
        <div class="header">${templateData.title}</div>
        
        ${sections.map(section => `
          <div class="section">
            <div class="section-content">
              <div class="section-title">${section.title}</div>
              <div class="section-description">${section.description}</div>
              ${section.buttonText ? `<a href="${section.buttonLink || '#'}" class="button">${section.buttonText}</a>` : ''}
            </div>
            ${section.imageUrl ? `<img src="${section.imageUrl}" alt="${section.title}" class="section-image">` : ''}
          </div>
        `).join('')}
        
        <div class="footer">
          This email has been sent to you because you are a customer or subscriber of SSM
        </div>
      </body>
      </html>
    `;
    };

    const handlePreview = async () => {
        try {
            setUploading(true);
            const html = generateHtmlFromTemplate(templateData.sections);
            setPreviewHtml(html);
            setOpenConfirm(true);
        } catch (error) {
            console.error('Error generating preview:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setUploading(true);

            const formData = new FormData();
            formData.append('subject', templateData.subject);
            formData.append('title', templateData.title);
            formData.append('isHtml', templateData.isHtml.toString());
            formData.append('save_as_template', saveTemplate);
            formData.append('template_name', templateName);

            if (template?.id) {
                formData.append('template_id', template.id);
            }

            formData.append('recipient_ids',
                selectAllMode ? 'ALL' : JSON.stringify(selectedIds));

            if (selectAllMode && filters) {
                const filtersData = typeof filters === 'string' ? JSON.parse(filters) : filters;
                formData.append('filters', JSON.stringify(filtersData));
            }

            templateData.sections.forEach((section, index) => {
                formData.append(`sections[${index}][title]`, section.title);
                formData.append(`sections[${index}][description]`, section.description);

                if (section.buttonText) {
                    formData.append(`sections[${index}][buttonText]`, section.buttonText);
                }

                if (section.buttonLink) {
                    formData.append(`sections[${index}][buttonLink]`, section.buttonLink);
                }

                if (section.imageFile) {
                    formData.append(`sections[${index}][imageFile]`, section.imageFile);
                } else if (section.imageUrl && !section.imageUrl.startsWith('data:')) {
                    formData.append(`sections[${index}][imageUrl]`, section.imageUrl);
                }
            });

            const response = await axios.post(
                api_Routes.Users.SendBulkEmailTemplate,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            navigate('/users', { state: { message: 'Emails queued for sending successfully!' } });
        } catch (error) {
            console.error('Failed to send emails:', error);
            if (error.response) {
                console.error('Validation errors:', error.response.data.errors);
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <Typography variant="h4" className="mb-6">
                {template?.id ? 'Edit Email Template' : 'Email Template Builder'}
            </Typography>

            <Paper elevation={3} className="p-6">
                <Box component="form" className="space-y-6">
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={saveTemplate}
                                    onChange={(e) => setSaveTemplate(e.target.checked)}
                                />
                            }
                            label="Save as template"
                        />

                        {saveTemplate && (
                            <TextField
                                fullWidth
                                label="Template Name"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                required
                                size="small"
                                sx={{ mt: 1, maxWidth: 400 }}
                            />
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        label="Email Subject"
                        value={templateData.subject}
                        onChange={(e) => setTemplateData({...templateData, subject: e.target.value})}
                        variant="outlined"
                        margin="normal"
                        required
                    />

                    <TextField
                        fullWidth
                        label="Email Title/Header"
                        value={templateData.title}
                        onChange={(e) => setTemplateData({...templateData, title: e.target.value})}
                        variant="outlined"
                        margin="normal"
                        required
                    />

                    <Divider className="my-4" />

                    <Typography variant="h6" className="mb-4">Email Sections</Typography>

                    {templateData.sections.map((section, index) => (
                        <Paper key={index} elevation={2} className="p-4 mb-4 relative">
                            <IconButton
                                onClick={() => handleRemoveSection(index)}
                                color="error"
                                className="absolute top-2 right-2"
                                disabled={templateData.sections.length <= 1}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="Section Title"
                                        value={section.title}
                                        onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                                        variant="outlined"
                                        margin="normal"
                                        required
                                    />

                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={section.description}
                                        onChange={(e) => handleSectionChange(index, 'description', e.target.value)}
                                        variant="outlined"
                                        margin="normal"
                                        multiline
                                        rows={3}
                                        required
                                    />

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Button Text"
                                                value={section.buttonText}
                                                onChange={(e) => handleSectionChange(index, 'buttonText', e.target.value)}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="Button Link"
                                                value={section.buttonLink}
                                                onChange={(e) => handleSectionChange(index, 'buttonLink', e.target.value)}
                                                variant="outlined"
                                                margin="normal"
                                                placeholder="https://example.com"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<CloudUploadIcon />}
                                        fullWidth
                                        sx={{ mt: 2, mb: 2 }}
                                        disabled={uploading}
                                    >
                                        Upload Image
                                        <VisuallyHiddenInput
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files[0]) {
                                                    handleImageChange(index, e.target.files[0]);
                                                }
                                            }}
                                        />
                                    </Button>

                                    {uploading && section.imageFile && (
                                        <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                            <CircularProgress />
                                        </Box>
                                    )}

                                    {(section.imageUrl || section.imageFile) && !(uploading && section.imageFile) && (
                                        <div className="mt-4 flex justify-center">
                                            <img
                                                src={section.imageUrl || URL.createObjectURL(section.imageFile)}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: '150px',
                                                    maxHeight: '150px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px'
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://placehold.co/150x150?text=Image+Error';
                                                }}
                                            />
                                        </div>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}

                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAddSection}
                        className="mb-4"
                    >
                        Add Section
                    </Button>

                    <Divider className="my-4" />

                    <div className="flex justify-end space-x-4 pt-4">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate(-1)}
                            size="large"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePreview}
                            size="large"
                            disabled={uploading}
                        >
                            {uploading ? 'Processing...' : 'Review and Send'}
                        </Button>
                    </div>
                </Box>
            </Paper>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="md" fullWidth>
                <DialogTitle>Confirm Email Sending</DialogTitle>
                <DialogContent>
                    <DialogContentText className="mb-4">
                        You are about to send this email to <strong>
                        {selectAllMode ? 'all filtered users' : `${selectedIds.length} selected users`}
                    </strong>. Please review the content:
                    </DialogContentText>

                    <Paper elevation={0} className="p-4 border rounded mb-4">
                        <Typography variant="subtitle1" className="font-bold">Subject:</Typography>
                        <Typography className="mb-4">{templateData.subject}</Typography>

                        <Typography variant="subtitle1" className="font-bold">Title/Header:</Typography>
                        <Typography className="mb-4">{templateData.title}</Typography>
                    </Paper>

                    <Typography variant="subtitle1" className="font-bold mb-2">Email Preview:</Typography>
                    <Paper elevation={0} className="p-4 border rounded">
                        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Send
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default EmailTemplate;